"use client";

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { Moon, Sun } from "lucide-react";

import styles from "./ThemeToggle.module.css";

type Theme = "dark" | "light";
type Props = { initialTheme: Theme };

function setCookieTheme(value: Theme) {
  const maxAge = 60 * 60 * 24 * 365;
  const isHttps =
    typeof location !== "undefined" && location.protocol === "https:";

  document.cookie = `theme=${value}; Max-Age=${maxAge}; Path=/; SameSite=Lax; ${
    isHttps ? "Secure" : ""
  }`;
}

function applyTheme(next: Theme) {
  const html = document.documentElement;
  const isDark = next === "dark";

  html.classList.toggle("dark", isDark);
  html.classList.toggle("light", !isDark);
  html.setAttribute("data-theme", next);
  html.style.colorScheme = next;
}

let listenersAttached = false;

export function ThemeToggle({ initialTheme }: Props) {
  const [isDark, setIsDark] = useState<boolean>(() => initialTheme === "dark");
  const mounted = useRef(false);

  useLayoutEffect(() => {
    const html = document.documentElement;
    const hasDark = html.classList.contains("dark");
    const hasLight = html.classList.contains("light");

    if (!hasDark && !hasLight) {
      applyTheme(isDark ? "dark" : "light");
    } else if (hasDark !== isDark) {
      setIsDark(hasDark);
    }
  }, [isDark]);

  useEffect(() => {
    if (listenersAttached) return;

    const onThemeChange = (e: Event) => {
      const detail = (e as CustomEvent).detail as { theme: Theme } | undefined;
      if (!detail) return;

      applyTheme(detail.theme);
      setIsDark(detail.theme === "dark");
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key === "theme" && e.newValue) {
        const next: Theme = e.newValue === "dark" ? "dark" : "light";
        applyTheme(next);
        setIsDark(next === "dark");
      }
    };

    window.addEventListener("themechange", onThemeChange as EventListener);
    window.addEventListener("storage", onStorage);
    listenersAttached = true;
    mounted.current = true;

    return () => {
      // singleton listeners blijven app-breed hangen
    };
  }, []);

  const toggleTheme = () => {
    const next: Theme = isDark ? "light" : "dark";

    applyTheme(next);
    localStorage.setItem("theme", next);
    setCookieTheme(next);
    setIsDark(next === "dark");
    window.dispatchEvent(
      new CustomEvent("themechange", { detail: { theme: next } })
    );
  };

  const toggleStyle = useMemo(
    () =>
      ({
        ["--toggle-x" as string]: isDark ? "30px" : "0px",
      }) as React.CSSProperties,
    [isDark]
  );

  return (
    <button
      onClick={toggleTheme}
      className={styles.toggleWrapper}
      aria-label="Toggle dark mode"
      type="button"
    >
      <div
        className={`${styles.toggle} ${isDark ? styles.active : ""}`}
        data-theme-toggle
        style={toggleStyle}
      >
        <div className={styles.icon}>
          <Sun size={16} />
        </div>
        <div className={styles.icon}>
          <Moon size={16} />
        </div>
        <div className={styles.slider}></div>
      </div>
    </button>
  );
}